import { CreateProductionCommand } from "@/models/productions/CreateProductionCommand";
import { apiClient } from "@/modules/apiClient";
import { Production } from "@/models/productions/Production";
import { AxiosResponse } from "axios";
import { ProductionMembership } from "@/models/productions/ProductionMembership";
import { AvatarViewModel } from "@/models/AvatarViewModel";
import { UpdateProductionCommand } from "@/models/productions/UpdateProductionCommand";
import { ExportProductionMeasurementsRequest } from "@/models/productions/ExportProductionMeasurementsRequest";
import { UserMeasurement } from "@/models/user/UserMeasurement";
import { ProductionOnboardingStatus } from "@/models/productions/ProductionOnboardingStatus";
import { ReportTemplateDto } from "@/models/reports/ReportTemplateDto";
import { UpdateSubscriptionDto } from "@/models/subscriptions/UpdateSubscriptionDto";
import { billingApiClient } from "@/modules/billingApiClient";
import { SubscriptionCheckoutSessionDto } from "@/models/subscriptions/SubscriptionCheckoutSessionDto";
import { CreateSubscriptionDto } from "@/models/subscriptions/CreateSubscriptionDto";

export const ProductionService = {
	list(): Promise<AxiosResponse<Array<ProductionMembership>>> {
		return apiClient.get(`/productions`);
	},
	listNew(): Promise<AxiosResponse<Array<Production>>> {
		return apiClient.get(`/productions/productions-list`);
	},
	fetch(productionId: string): Promise<AxiosResponse<Production>> {
		return apiClient.get(`/productions/${productionId}`);
	},
	fetchQuery: async (productionId: string): Promise<Production> => {
		const response = await apiClient.get(`/productions/${productionId}`);
		return response.data;
	},
	create(command: CreateProductionCommand, organizationId: string): Promise<AxiosResponse<Production>> {
		return apiClient.post(`/productions`, command);
	},
	update(productionId: string, command: UpdateProductionCommand): Promise<AxiosResponse> {
		return apiClient.put(`/productions/${productionId}`, command);
	},
	archive(productionId: string): Promise<AxiosResponse> {
		return apiClient.post(`/productions/${productionId}/archive`);
	},
	restore(productionId: string): Promise<AxiosResponse> {
		return apiClient.post(`/productions/${productionId}/restore`);
	},
	delete(productionId: string): Promise<AxiosResponse> {
		return apiClient.delete(`/productions/${productionId}/self`);
	},
	fetchOnboardStatus(productionId: string): Promise<AxiosResponse<ProductionOnboardingStatus>> {
		return apiClient.get(`/productions/${productionId}/onboarding-status`);
	},
	uploadPhoto(productionId: string, formData): Promise<AxiosResponse<AvatarViewModel>> {
		return apiClient.put(`/productions/${productionId}/photo`, formData, { headers: { "Content-Type": "multipart/form-data" } });
	},
	removePhoto(productionId: string): Promise<AxiosResponse> {
		return apiClient.delete(`/productions/${productionId}/photo`);
	},
	exportProductionMeasurements(command: ExportProductionMeasurementsRequest): Promise<AxiosResponse> {
		return apiClient.get(`/measurements/export/${command.productionId}?format=${command.format}`, {
			responseType: "blob",
			params: { groups: command.groups },
		});
	},
	listProductionMeasurements(productionId: string): Promise<AxiosResponse<Array<UserMeasurement>>> {
		return apiClient.get(`/measurements/production-list?productionId=${productionId}`);
	},
	async activate(productionId: string, input: any): Promise<SubscriptionCheckoutSessionDto> {
		const response = await apiClient.post(`/productions/${productionId}/activate`, input);
		return response.data;
	},
	async updateSubscription(subscriptionId: string, input: UpdateSubscriptionDto): Promise<SubscriptionCheckoutSessionDto> {
		const response = await billingApiClient.put(`/subscriptions/${subscriptionId}`, input);
		return response.data;
	},
	async cancelSubscription(subscriptionId: string, refundBalance: boolean): Promise<SubscriptionCheckoutSessionDto> {
		const response = await billingApiClient.delete(`/subscriptions/${subscriptionId}?refundBalance=${refundBalance}`);
		return response.data;
	},
	async getCustomerPortalUrl(organizationId: string): Promise<SubscriptionCheckoutSessionDto> {
		const response = await billingApiClient.get(`/subscriptions/${organizationId}/customer-portal`, {
			params: {
				organizationId: organizationId,
				returnUrl: window.location.href,
			},
		});

		return response.data;
	},
	async createSubscription(param: CreateSubscriptionDto) {
		const response = await billingApiClient.post(`/subscriptions`, param);
		return response.data;
	},
};
